/**
 * How to report the result of recording line decisions.
 *
 * The server never fails a batch because one line couldn't be decided — it
 * records what it can and returns the rest in `skipped` with a reason. That
 * makes partial success the normal case for a bulk Accept or Disagree, so the
 * report has to say what actually happened rather than just "saved": someone who
 * selected 14 lines and had 2 skipped needs to know which number was which,
 * without opening the timeline to count.
 *
 * This module classifies the outcome and carries NO user-facing text. The words
 * are built by the caller through `gt()`, with a separate string per decision
 * rather than a verb interpolated into one template — word order and
 * pluralization don't survive that.
 */

/** Why the server declined a line — a stable code, translated for display. */
export type SkippedReasonCode = 'line-not-found' | 'not-decidable';

export interface LineDecisionOutcome {
  /** How many rows the server wrote. */
  recorded: number;
  /**
   * Lines it declined to decide. `reasonCode` is what gets shown (translated);
   * `reason` is the server's English prose, kept for logs and absent on an older
   * server.
   */
  skipped: readonly {
    lineIndex: number;
    reasonCode?: SkippedReasonCode;
    reason?: string;
  }[];
}

export type LineDecisionReport =
  /** Nothing to report — see `classifyLineDecisionOutcome`. */
  | { kind: 'silent' }
  /** Every requested line was declined. */
  | {
      kind: 'all-skipped';
      skipped: number;
      /** Set only when a single line was declined, so one reason describes it. */
      reasonCode: SkippedReasonCode | null;
    }
  /** Some recorded, some declined — the user got less than they asked for. */
  | {
      kind: 'partial';
      recorded: number;
      skipped: number;
      reasonCode: SkippedReasonCode | null;
    }
  /** A clean multi-line write. */
  | { kind: 'recorded'; recorded: number };

/**
 * Classify an outcome for reporting.
 *
 * Two cases are deliberately silent:
 *  - a single line recorded cleanly — the row itself visibly changes, and a
 *    toast per click is noise;
 *  - nothing written and nothing skipped — a re-click of the decision the line
 *    already carries, which the server keeps as a no-op (preserving the original
 *    author and timestamp). There is nothing to say about it.
 */
export function classifyLineDecisionOutcome(
  outcome: LineDecisionOutcome,
): LineDecisionReport {
  const { recorded } = outcome;
  const skipped = outcome.skipped.length;
  // One reason is worth naming; several means the selection was mixed and the
  // count is the useful part.
  const reasonCode =
    skipped === 1 ? (outcome.skipped[0]?.reasonCode ?? null) : null;

  if (recorded === 0 && skipped === 0) return { kind: 'silent' };
  if (recorded === 0) return { kind: 'all-skipped', skipped, reasonCode };
  if (skipped > 0) return { kind: 'partial', recorded, skipped, reasonCode };
  if (recorded === 1) return { kind: 'silent' };
  return { kind: 'recorded', recorded };
}
